import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { AuthField } from "@/components/AuthField";
import { BuildEaseLogo } from "@/components/BuildEaseLogo";
import { CADGridBackground } from "@/components/CADGridBackground";
import { HUDCheckbox } from "@/components/HUDCheckbox";
import { PasswordStrengthBar, scorePassword } from "@/components/PasswordStrengthBar";
import { useAuthStore } from "@/store/authStore";

const EASE = [0.22, 1, 0.36, 1] as const;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const item = {
  hidden: { opacity: 0, y: 18 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.12 + i * 0.08, duration: 0.6, ease: EASE },
  }),
};

type Errors = { name?: string; email?: string; password?: string; terms?: boolean };

export default function SignupScreen() {
  const navigate = useNavigate();
  const signup = useAuthStore((s) => s.signup);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);

  const clear = (k: keyof Errors) => setErrors((p) => ({ ...p, [k]: undefined }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const next: Errors = {};
    if (name.trim().length < 2) next.name = "Enter your full name";
    if (!EMAIL_RE.test(email)) next.email = "Enter a valid email";
    if (scorePassword(password) < 1) next.password = "Min. 6 characters";
    if (!agreed) next.terms = true;
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;
    setSubmitting(true);
    await signup(name.trim(), email);
    navigate("/onboarding/personal", { replace: true });
  };

  return (
    <div
      data-testid="signup-screen"
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-obsidian px-6 py-12"
    >
      <CADGridBackground opacity={0.28} />
      <div
        aria-hidden
        className="absolute left-1/2 top-[40%] h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-safety/[0.05] blur-3xl"
      />

      <motion.form
        onSubmit={onSubmit}
        noValidate
        initial="hidden"
        animate="show"
        className="relative flex w-full max-w-sm flex-col gap-7"
      >
        <motion.div variants={item} custom={0}>
          <BuildEaseLogo height={40} />
        </motion.div>

        <div className="flex flex-col gap-2">
          <div className="overflow-hidden">
            <motion.h1
              data-testid="signup-headline"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ delay: 0.2, duration: 0.7, ease: EASE }}
              className="font-display text-4xl font-bold tracking-tight text-white sm:text-5xl"
            >
              Create your account
            </motion.h1>
          </div>
          <motion.p
            variants={item}
            custom={2}
            data-testid="signup-subtext"
            className="font-sans text-sm text-white/50"
          >
            Start building with clarity.
          </motion.p>
        </div>

        <div className="flex flex-col gap-4">
          <motion.div variants={item} custom={3}>
            <AuthField
              label="Full Name"
              index="01"
              testId="signup-name-input"
              type="text"
              autoComplete="name"
              placeholder="Amara Okafor"
              value={name}
              error={errors.name}
              onChange={(e) => { setName(e.target.value); clear("name"); }}
            />
          </motion.div>
          <motion.div variants={item} custom={4}>
            <AuthField
              label="Email"
              index="02"
              testId="signup-email-input"
              type="email"
              autoComplete="email"
              placeholder="you@site.com"
              value={email}
              error={errors.email}
              onChange={(e) => { setEmail(e.target.value); clear("email"); }}
            />
          </motion.div>
          <motion.div variants={item} custom={5} className="flex flex-col gap-2.5">
            <AuthField
              label="Password"
              index="03"
              testId="signup-password-input"
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              value={password}
              error={errors.password}
              onChange={(e) => { setPassword(e.target.value); clear("password"); }}
            />
            <PasswordStrengthBar password={password} />
          </motion.div>
        </div>

        <motion.div variants={item} custom={6} className="flex flex-col gap-1.5">
          <HUDCheckbox
            testId="signup-terms-checkbox"
            checked={agreed}
            error={errors.terms}
            onChange={(v) => { setAgreed(v); clear("terms"); }}
          >
            I agree to the{" "}
            <Link
              to="/legal/terms"
              data-testid="signup-terms-link"
              className="font-medium text-safety underline-offset-4 transition-colors duration-200 hover:text-white hover:underline"
            >
              Terms
            </Link>{" "}
            &amp;{" "}
            <Link
              to="/legal/privacy"
              data-testid="signup-privacy-link"
              className="font-medium text-safety underline-offset-4 transition-colors duration-200 hover:text-white hover:underline"
            >
              Privacy Policy
            </Link>
          </HUDCheckbox>
          <AnimatePresence>
            {errors.terms && (
              <motion.p
                data-testid="signup-terms-error"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="pl-8 font-mono text-[11px] uppercase tracking-[0.14em] text-red-400"
              >
                Accept the terms to continue
              </motion.p>
            )}
          </AnimatePresence>
        </motion.div>

        <motion.div variants={item} custom={7}>
          <motion.button
            type="submit"
            data-testid="signup-submit-button"
            disabled={submitting}
            whileHover={{ scale: submitting ? 1 : 1.015 }}
            whileTap={{ scale: submitting ? 1 : 0.98 }}
            transition={{ type: "spring", stiffness: 400, damping: 24 }}
            className="group relative flex h-14 w-full items-center justify-center overflow-hidden rounded-2xl bg-safety font-display text-base font-semibold text-white shadow-[0_12px_40px_-12px_rgba(255,91,0,0.7)] disabled:cursor-wait"
          >
            <span
              aria-hidden
              className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full"
            />
            <AnimatePresence mode="wait" initial={false}>
              {submitting ? (
                <motion.span
                  key="loading"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em]"
                >
                  <Loader2 size={16} className="animate-spin" />
                  Provisioning
                </motion.span>
              ) : (
                <motion.span
                  key="idle"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  className="flex items-center gap-2"
                >
                  Create Account
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </motion.div>

        <motion.p
          variants={item}
          custom={8}
          data-testid="signup-login-text"
          className="text-center font-sans text-sm text-white/50"
        >
          Already have an account?{" "}
          <Link
            to="/login"
            data-testid="signup-login-link"
            className="font-medium text-safety underline-offset-4 transition-colors duration-200 hover:text-white hover:underline"
          >
            Log in
          </Link>
        </motion.p>
      </motion.form>
    </div>
  );
}
